import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

/**
 * La collecte des variables du mois, vue du cabinet.
 *
 * Les décisions — qui relancer, qui laisser tranquille — sont prises côté
 * serveur, pas ici. L'écran ne fait qu'afficher ce qu'on lui dit : c'est ce qui
 * garantit qu'un client ayant répondu ne sera jamais relancé parce qu'un
 * affichage était en retard d'un rafraîchissement.
 */
const collecteApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/saisie-client/collecte',
  headers: { 'Content-Type': 'application/json' },
});
tokenManager.setupInterceptors(collecteApi);

export type EtatCollecte =
  | 'sans_lien'
  | 'attendu'
  | 'recu'
  | 'recu_autrement'
  | 'revoque';

export type EtatEtape = 'fait' | 'echec' | 'attendu' | 'en_retard';

/** Une case du tableau : ce qu'elle dit, et si elle doit alerter. */
export interface Etape {
  etat: EtatEtape;
  texte: string;
  date: string | null;
}

export interface LigneCollecte {
  folderUuid: string;
  dossier: string;
  etat: EtatCollecte;
  bulletins: Etape;
  dsn: Etape;
  soumisLe: string | null;
  recuHorsLien: string | null;
  recuHorsLienMotif: string | null;
  derniereRelance: string | null;
  relances: number;
  /**
   * Depuis combien de jours les variables attendent sans que les bulletins
   * soient faits. `null` tant qu'il n'y a rien à signaler — c'est le serveur
   * qui juge, pas l'écran.
   */
  enSouffranceDepuis: number | null;
  relancable: boolean;
  motifSiNonRelancable: string | null;
}

export interface BilanRelance {
  envoyees: number;
  ignorees: number;
  detail: { dossier: string; envoye: boolean; motif?: string }[];
}

export const collecteService = {
  /** Où en est chaque dossier du cabinet pour ce mois. */
  duMois: async (mois: number, annee: number): Promise<LigneCollecte[]> => {
    const r = await collecteApi.get<{ data: LigneCollecte[] }>(
      `?mois=${mois}&annee=${annee}`
    );
    return r.data.data ?? (r.data as unknown as LigneCollecte[]);
  },

  /**
   * Relance ceux qui n'ont rien envoyé.
   *
   * On n'envoie AUCUNE liste : c'est la règle du serveur qui choisit. Laisser
   * l'écran désigner les dossiers rouvrirait la porte qu'on ferme — celle où
   * l'on écrit à un client qui a déjà répondu.
   */
  relancer: async (mois: number, annee: number): Promise<BilanRelance> => {
    const r = await collecteApi.post<{ data: BilanRelance }>('/relancer', {
      mois,
      annee,
      origine: window.location.origin,
    });
    return r.data.data ?? (r.data as unknown as BilanRelance);
  },

  /** Désigner un envoi arrivé hors du lien — un mail, un appel. */
  recuAutrement: async (
    folderUuid: string,
    mois: number,
    annee: number,
    motif: string
  ): Promise<void> => {
    await collecteApi.post(`/${folderUuid}/recu-autrement`, {
      mois,
      annee,
      motif,
    });
  },

  /**
   * Ouvrir le formulaire du mois pour saisir À LA PLACE du client.
   *
   * C'est le formulaire du client lui-même qui revient — pas une copie faite
   * pour le cabinet. Le lien que le client a reçu n'est ni révoqué ni
   * remplacé : il reste utilisable.
   */
  formulaire: async (
    folderUuid: string,
    mois: number,
    annee: number
  ): Promise<unknown> => {
    const { data } = await collecteApi.get(`/${folderUuid}/formulaire`, {
      params: { mois, annee },
    });
    return data?.data ?? data;
  },

  /**
   * Enregistrer ce que la gestionnaire a saisi pour ce client.
   *
   * Ce qui est enregistré compte comme une réception : les relances s'arrêtent
   * d'elles-mêmes, sans qu'elle ait à cocher quoi que ce soit.
   */
  saisirPourLeClient: async (
    folderUuid: string,
    mois: number,
    annee: number,
    lignes: unknown[]
  ): Promise<{ enregistrees: number }> => {
    const { data } = await collecteApi.post(`/${folderUuid}/saisir`, {
      mois,
      annee,
      lignes,
    });
    return data?.data ?? data;
  },

  /** Retirer une désignation posée sur le mauvais dossier. */
  annulerRecuAutrement: async (
    folderUuid: string,
    mois: number,
    annee: number
  ): Promise<void> => {
    await collecteApi.post(`/${folderUuid}/annuler-recu-autrement`, {
      mois,
      annee,
    });
  },
};
